import { BigNumber } from 'bignumber.js';
import { hundred, suffixInterest, thousand } from 'constants/number';
import {
  billion,
  million,
  one,
  oneThousandth,
  ten,
  zero,
} from 'constants/zero';
import { LoanData } from 'app/pages/NewLoan/store/loanStore';
import { TokenDetail } from 'store/type';
import { Vault } from 'types';
import { ethers } from 'ethers';
import { amountFromWei } from 'utils/contract/common-function';
import { SubmittableExtrinsic } from '@polkadot/api/types';
import { KeyringPair } from '@polkadot/keyring/types';
import { EventRecord, ExtrinsicStatus } from '@polkadot/types/interfaces';
import { waitFor } from 'utils/waitFor';
import { notifyError, notifySuccess } from 'utils/helpers';

interface FormatPercentOptions {
  precision?: number;
  plus?: boolean;
  roundMode?: BigNumber.RoundingMode;
}
export function formatPercent(
  number: BigNumber,
  {
    precision = 0,
    plus = false,
    roundMode = undefined,
  }: FormatPercentOptions = {},
) {
  const sign = plus && number.isGreaterThan(0) ? '+' : '';

  return `${sign}${number.toFixed(precision, roundMode)}%`;
}
export function toShorthandNumber(
  amount: BigNumber,
  suffix: string = '',
  precision?: number,
) {
  const sh = new BigNumber(
    amount
      .toString()
      .split('.')
      .map((part, index) => {
        if (index === 0) return part;
        return part.substr(0, precision);
      })
      .filter(el => el)
      .join('.'),
  );
  if (precision) {
    return sh.toFixed(precision).concat(suffix);
  }
  return sh.toFixed().concat(suffix);
}

export function formatAsShorthandNumbers(
  amount: BigNumber,
  precision?: number,
): string {
  if (amount.absoluteValue().gte(billion)) {
    return toShorthandNumber(amount.dividedBy(billion), 'B', precision);
  }
  if (amount.absoluteValue().gte(million)) {
    return toShorthandNumber(amount.dividedBy(million), 'M', precision);
  }
  if (amount.absoluteValue().gte(thousand)) {
    return toShorthandNumber(amount.dividedBy(thousand), 'K', precision);
  }
  return toShorthandNumber(amount, '', precision);
}

export function formatCryptoBalance(amount: BigNumber): string {
  const absAmount = amount.absoluteValue();

  if (absAmount.eq(zero)) {
    return formatAsShorthandNumbers(amount, 2);
  }

  if (absAmount.lt(oneThousandth)) {
    return `${amount.isNegative() ? '0.000' : '<0.001'}`;
  }

  if (absAmount.lt(ten)) {
    return formatAsShorthandNumbers(amount, 4);
  }

  if (absAmount.lt(million)) return amount.toFormat(2, BigNumber.ROUND_DOWN);

  return formatAsShorthandNumbers(amount, 2);
}

export function checkLoanOwner(proxyAddress?: string, loanOwner?: string) {
  // if (!proxyAddress || !loanOwner) return false;
  // return proxyAddress === loanOwner;
  return true;
}

export function formatFiatBalance(amount?: BigNumber): string {
  if (!amount) return '';
  const absAmount = amount.absoluteValue();

  if (absAmount.eq(zero)) return formatAsShorthandNumbers(amount, 2);
  if (absAmount.lt(one)) return formatAsShorthandNumbers(amount, 4);
  if (absAmount.lt(million)) return amount.toFormat(2, BigNumber.ROUND_DOWN);
  // We don't want to have numbers like 999999 formatted as 999.99k

  return formatAsShorthandNumbers(amount, 2);
}

export function isNullish(amount: BigNumber | undefined | null): boolean {
  return !amount || amount.isZero();
}

export function allInputNull(data: {
  [X: string]: BigNumber | undefined | null;
}): boolean {
  const { depositAmount, withdrawAmount, generateAmount, paybackAmount } = data;
  return (
    isNullish(depositAmount) &&
    isNullish(withdrawAmount) &&
    isNullish(generateAmount) &&
    isNullish(paybackAmount)
  );
}

export function parseDataFromContract<T>(state) {
  return Object.entries(state)
    .map(([key, value]) => [
      key,
      typeof value === 'string' ? (value as any)?.replaceAll(',', '') : value,
    ])
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as T);
}

export function parseLoanModel(state?: TokenDetail): LoanData {
  //console.log(state)
  if (!state) {
    return {
      collateralizationDangerThreshold: zero,
      collateralizationWarningThreshold: zero,
      currentPrice: zero,
      currentPriceUpdate: undefined,
      debtCeiling: zero,
      debtFloor: zero,
      debtScalingFactor: zero,
      feeLastLevied: undefined,
      ilk: '',
      ilkDebt: zero,
      ilkDebtAvailable: zero,
      isStaticPrice: false,
      liquidationPenalty: zero,
      liquidationRatio: zero,
      liquidatorAddress: ethers.constants.AddressZero,
      maxAuctionLotSize: zero,
      maxDebtPerUnitCollateral: zero,
      nextPrice: zero,
      nextPriceUpdate: undefined,
      normalizedIlkDebt: zero,
      percentageChange: zero,
      priceFeedAddress: ethers.constants.AddressZero,
      priceUpdateInterval: undefined,
      stabilityFee: zero,
      token: '',
      detailData: undefined,
      exchangeRate: zero,
      constDebt: zero,
      totalDebit: zero,
    };
  }

  const parseData = parseDataFromContract(state) as TokenDetail;

  console.log(parseData);

  const exchangeRate = parseData.exchangeRate
    ? amountFromWei(new BigNumber(parseData.exchangeRate), 18)
    : zero;

  const totalDebit = parseData.totalDebit?.debit
    ? amountFromWei(new BigNumber(parseData.totalDebit.debit), 18)
    : zero;
  const ilkDebtAvailable = amountFromWei(
    new BigNumber(parseData.maximumTotalDebitValue),
    18,
  ).minus(totalDebit.multipliedBy(exchangeRate));

  return {
    constDebt: zero,
    collateralizationDangerThreshold: parseData.requiredCollateralRatio
      ? amountFromWei(new BigNumber(parseData.requiredCollateralRatio), 18)
      : zero,
    collateralizationWarningThreshold: parseData.notifiedCollateralRatio
      ? amountFromWei(new BigNumber(parseData.notifiedCollateralRatio), 18)
      : zero,
    currentPrice: amountFromWei(new BigNumber(parseData.price), 18),
    currentPriceUpdate: undefined,
    debtCeiling: zero,
    debtFloor: hundred,
    debtScalingFactor: zero,
    feeLastLevied: undefined,
    ilk: state.tokenId,
    ilkDebt: zero,
    ilkDebtAvailable,
    isStaticPrice: false,
    liquidationPenalty: amountFromWei(
      new BigNumber(parseData.liquidationPenalty || zero),
      18,
    ),
    liquidationRatio: amountFromWei(
      new BigNumber(parseData.liquidationRatio),
      18,
    ),
    liquidatorAddress: ethers.constants.AddressZero,
    maxAuctionLotSize: zero,
    maxDebtPerUnitCollateral: zero,
    nextPrice: amountFromWei(new BigNumber(parseData.price), 18),
    nextPriceUpdate: undefined,
    normalizedIlkDebt: zero,
    percentageChange: zero,
    priceFeedAddress: ethers.constants.AddressZero,
    priceUpdateInterval: undefined,
    stabilityFee: parseData.interestRatePerSec
      ? amountFromWei(
          new BigNumber(parseData.interestRatePerSec),
          18,
        ).multipliedBy(suffixInterest)
      : zero,
    token: state.token,
    detailData: undefined,
    exchangeRate,
    totalDebit,
  };
}

export async function execute(
  extrinsic: SubmittableExtrinsic<'promise'>,
  address: string,
  singer: any,
  onSuccess: (tx) => void,
  onFailed: (tx) => void,
  updateStatus?: (status: string) => void,
  logger = { info: console.log },
): Promise<void> {
  let currentTxDone = false;

  function sendStatusCb({
    events = [],
    status,
  }: {
    events?: EventRecord[];
    status: ExtrinsicStatus;
  }) {
    if (status.isInvalid) {
      logger.info('Transaction invalid');
      updateStatus && updateStatus('Transaction invalid!');
      currentTxDone = true;
    } else if (status.isReady) {
      logger.info('Transaction is ready');
      updateStatus && updateStatus('Transaction is ready');
    } else if (status.isBroadcast) {
      logger.info('Transaction has been broadcasted');
      updateStatus && updateStatus('Broadcasted...');
    } else if (status.isInBlock) {
      logger.info('Transaction is in block');
      updateStatus && updateStatus('Transaction is in block');
    } else if (status.isFinalized) {
      logger.info(
        `Transaction has been included in blockHash ${status.asFinalized.toHex()}`,
      );
      updateStatus && updateStatus('Included in block');
      events.forEach(({ event }) => {
        if (event.method === 'ExtrinsicSuccess') {
          logger.info('Transaction succeeded');
          notifySuccess('Transaction succeeded');
          onSuccess(status.asFinalized.toHex());
        } else if (event.method === 'ExtrinsicFailed') {
          logger.info('Transaction failed');
          notifyError('Transaction failed');
          onFailed(status.asFinalized.toHex());
        }
      });
      currentTxDone = true;
    }
  }

  await extrinsic.signAndSend(address, singer, sendStatusCb);
  await waitFor(() => currentTxDone, { timeout: 20000 });
}
